import { ProfilesBadgeList } from './ProfilesBadgeList';
import { formatRelativeDate, formatAbsoluteDateTime } from '../../lib/formatters';
import type { ExtranetUserListItem } from '../../types/extranet-api.types';
import { IconInbox } from '../incidents/icons';

interface UsersTableProps {
  items: ExtranetUserListItem[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  canManage: boolean;
  onEdit: (user: ExtranetUserListItem) => void;
  onToggleStatus: (user: ExtranetUserListItem) => void;
  onDelete: (user: ExtranetUserListItem) => void;
}

const SKELETON_ROWS = 8;

export const UsersTable = ({
  items,
  total,
  isLoading,
  isFetching,
  canManage,
  onEdit,
  onToggleStatus,
  onDelete,
}: UsersTableProps) => (
  <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/40">
            <Th>Nom</Th>
            <Th>Prénom</Th>
            <Th>Email</Th>
            <Th>Profil(s)</Th>
            <Th align="right">Périmètre</Th>
            <Th>Statut</Th>
            <Th>Dernière connexion</Th>
            {canManage ? <Th align="right">Actions</Th> : null}
          </tr>
        </thead>
        <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} hasActions={canManage} />)
          ) : items.length === 0 ? (
            <EmptyRow colSpan={canManage ? 8 : 7} />
          ) : (
            items.map(user => (
              <tr
                key={user.id}
                className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60 transition-colors duration-100"
              >
                <td className="px-4 py-3 text-zinc-900 whitespace-nowrap font-medium">{user.lastName}</td>
                <td className="px-4 py-3 text-zinc-700 whitespace-nowrap">{user.firstName}</td>
                <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-700 whitespace-nowrap">
                  {user.email}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <ProfilesBadgeList profiles={user.profiles} />
                </td>
                <td className="px-4 py-3 text-right text-xs text-zinc-500 whitespace-nowrap">
                  <span className="font-mono tabular-nums">{user.scope.entityIds.length}</span> ent. ·{' '}
                  <span className="font-mono tabular-nums">{user.scope.contractIds.length}</span> contrats
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={[
                      'inline-flex items-center gap-1.5 rounded-md text-xs font-medium px-2 py-0.5',
                      user.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500',
                    ].join(' ')}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${user.status === 'active' ? 'bg-emerald-500' : 'bg-zinc-400'}`}
                      aria-hidden
                    />
                    {user.status === 'active' ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td
                  className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-500 whitespace-nowrap"
                  title={user.lastLoginAt ? formatAbsoluteDateTime(user.lastLoginAt) : 'Jamais connecté'}
                >
                  {user.lastLoginAt ? formatRelativeDate(user.lastLoginAt) : '—'}
                </td>
                {canManage ? (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => onEdit(user)}
                        className="text-zinc-700 hover:text-zinc-900 hover:underline underline-offset-2"
                      >
                        Modifier
                      </button>
                      <span className="text-zinc-300">·</span>
                      <button
                        type="button"
                        onClick={() => onToggleStatus(user)}
                        className="text-zinc-700 hover:text-zinc-900 hover:underline underline-offset-2"
                      >
                        {user.status === 'active' ? 'Désactiver' : 'Réactiver'}
                      </button>
                      <span className="text-zinc-300">·</span>
                      <button
                        type="button"
                        onClick={() => onDelete(user)}
                        className="text-red-600 hover:text-red-700 hover:underline underline-offset-2"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    {!isLoading && items.length > 0 ? (
      <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100">
        <span className="font-mono tabular-nums text-zinc-700">{items.length}</span> sur{' '}
        <span className="font-mono tabular-nums text-zinc-700">{total}</span> utilisateurs
      </div>
    ) : null}
  </div>
);

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SkeletonRow = ({ hasActions }: { hasActions: boolean }) => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: hasActions ? 8 : 7 }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3 rounded-sm skeleton-pulse"
          style={{ width: `${[60, 55, 80, 50, 40, 45, 50, 35][i] ?? 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucun utilisateur sur ce compte.</p>
          <p className="text-xs text-zinc-500 mt-1">Créez un premier utilisateur via « + Nouvel utilisateur ».</p>
        </div>
      </div>
    </td>
  </tr>
);
